/**
 * Block M1a (TZ 2026-07-29) + M1a-BE tail + M1b: Matching hooks (module-level).
 *
 * Все хуки живут на module-level (не dynamic import внутри onClick) и
 * invalidate-ят соответствующие query keys на success — НЕ location.reload.
 *
 * - useStartMatching — POST /pipeline/start с module_keys=['matching'] +
 *   matching_template_id / matching_language / matching_stage / is_test_run.
 * - usePipelineMatchingRows — GET /content/pipeline/{id}/rows (useGeneration re-exported).
 * - useConfirmCategories — POST /pipelines/{id}/matching/confirm-categories.
 * - useRemoveCategory — POST /pipelines/{id}/matching/remove-category.
 * - useResolveRow — POST /datasets/{id}/matching/resolve-row.
 * - useStartProperties (M1b) — POST /pipelines/{id}/matching/start-properties.
 * - useDeleteProperty (M1b) — DELETE /pipelines/{id}/matching/properties/{key}.
 * - usePatchProperty (M1b) — PATCH /pipelines/{id}/matching/properties/{key}.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import api from '@/lib/api';
import {
  type PipelineRun,
  type PipelineStartBody,
  usePipelineRows,
} from './useGeneration';

export {
  usePipelineRows as usePipelineMatchingRows,
};

// === Types ===

export type MatchingStage = 'categories' | 'properties';

export interface MatchingCategoryCandidate {
  path_ua: string[];
  path_ru: string[];
  category_id: string;
  score: number;
  status: 'proposed' | 'confirmed' | 'removed';
}

export interface MatchingMappedData {
  schema_version?: number;
  stage?: 'empty' | 'categories' | 'awaiting_confirm' | 'properties' | 'done';
  language?: string;
  template_id?: string | null;
  rows?: Record<string, MatchingRowEntry>;
  aggregate?: Array<{ key: string; path_label?: string; row_count: number }>;
  confidence?: { category_stage_completed?: boolean };
  confirm_token?: string | null;
}

export interface MatchingRowEntry {
  status: 'matched' | 'disputed' | 'unresolved' | 'manual';
  category?: MatchingCategoryCandidate | null;
  candidates?: MatchingCategoryCandidate[];
  properties?: Record<string, unknown>;
  meta?: Record<string, unknown>;
}

export interface MatchingManualPropertyInput {
  name: string;
  unit?: string | null;
  allowed_values?: string[] | null;
  /** §D.4: fill mode ('only_mine' default). */
  mode?: 'only_mine' | 'by_rule';
  /** §D.4: deterministic formatting rule text (by_rule mode). */
  format_rule?: string | null;
}

export interface MatchingStartBody {
  dataset_id: string;
  tier_key: 'economy' | 'optimal' | 'max';
  module_keys: ['matching'];
  matching_template_id: string | null;
  matching_language: 'ua' | 'ru';
  matching_stage: MatchingStage;
  matching_confirm_token?: string | null;
  // M1a-FE additions (TZ 2026-07-29): matching_manual_properties +
  // matching_input_columns + matching_multivalue_delimiter прокидываются через
  // Pydantic extra='allow' в PipelineStartRequest (backend BE готов).
  matching_manual_properties?: MatchingManualPropertyInput[] | null;
  matching_input_columns?: string[] | null;
  matching_multivalue_delimiter?: ',' | ';' | '|' | '\n';
  is_test_run: boolean;
  test_run_limit: number;
}

// === Mutations ===

/** Compatibility shim — converts MatchingStartBody → PipelineStartBody. */
export function useStartMatching() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: MatchingStartBody) => {
      const pl: PipelineStartBody = {
        dataset_id: body.dataset_id,
        tier_key: body.tier_key,
        module_keys: body.module_keys,
        output_language: body.matching_language === 'ru' ? 'Russian' : 'Ukrainian',
        is_test_run: body.is_test_run,
        test_run_limit: body.test_run_limit,
      };
      // Backend /pipeline/start Pydantic-extra='allow' (?) — явная передача matching_*
      // (см. backend/src/schemas/pipeline.py PipelineStartRequest).
      const response = await api.post<PipelineRun>('/pipeline/start', {
        ...pl,
        matching_template_id: body.matching_template_id,
        matching_language: body.matching_language,
        matching_stage: body.matching_stage,
        matching_confirm_token: body.matching_confirm_token,
        // M1a-FE additions — prкадятся «как есть», BE принимает через extra='allow'.
        matching_manual_properties: body.matching_manual_properties ?? null,
        matching_input_columns: body.matching_input_columns ?? null,
        matching_multivalue_delimiter: body.matching_multivalue_delimiter ?? ',',
      });
      return response.data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['pipeline'] });
      qc.setQueryData(['pipeline', data.id], data);
      qc.invalidateQueries({
        queryKey: ['pipeline', 'latest-by-dataset', data.dataset_id],
      });
    },
  });
}

export interface ConfirmCategoriesResponse {
  pipeline_id: string;
  confirm_token: string;
  matched_rows: number;
  unmatched_rows: number;
  completed_at: string;
}

export function useConfirmCategories() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (pipelineId: string) => {
      const r = await api.post<ConfirmCategoriesResponse>(
        `/pipelines/${pipelineId}/matching/confirm-categories`,
        {},
      );
      return r.data;
    },
    onSuccess: (_data, pipelineId) => {
      qc.invalidateQueries({ queryKey: ['pipeline', pipelineId, 'rows'] });
      qc.invalidateQueries({ queryKey: ['pipeline', pipelineId] });
    },
  });
}

export interface RemoveCategoryInput {
  pipeline_id: string;
  category_key: string;
}

export interface RemoveCategoryResponse {
  pipeline_id: string;
  category_key: string;
  affected_row_count: number;
}

export function useRemoveCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RemoveCategoryInput) => {
      const response = await api.post<RemoveCategoryResponse>(
        `/pipelines/${input.pipeline_id}/matching/remove-category`,
        { category_key: input.category_key },
      );
      return response.data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['pipeline', vars.pipeline_id, 'rows'] });
      qc.invalidateQueries({ queryKey: ['pipeline', vars.pipeline_id] });
    },
  });
}

export interface ResolveRowInput {
  dataset_id: string;
  row_id: string;
  category: MatchingCategoryCandidate;
}

export interface ResolveRowResponse {
  dataset_id: string;
  row_id: string;
  status: string;
  category: MatchingCategoryCandidate;
}

export function useResolveRow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ResolveRowInput) => {
      const r = await api.post<ResolveRowResponse>(
        `/datasets/${input.dataset_id}/matching/resolve-row`,
        { row_id: input.row_id, category: input.category },
      );
      return r.data;
    },
    onSuccess: () => {
      // Invalidate все pipeline-rows queries (как минимум — by-dataset siblings).
      qc.invalidateQueries({ queryKey: ['pipeline'] });
    },
  });
}

// === M1b: property stage endpoints ===

export interface StartPropertiesInput {
  pipeline_id: string;
  confirm_token: string;
  multivalue_overrides?: Record<string, boolean> | null;
  delimiter?: ',' | ';' | '|' | '\n' | null;
}

export interface StartPropertiesResponse {
  pipeline_id: string;
  stage: string;
  properties_extracted: number;
  property_keys: string[];
}

export function useStartProperties() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: StartPropertiesInput) => {
      const r = await api.post<StartPropertiesResponse>(
        `/pipelines/${input.pipeline_id}/matching/start-properties`,
        {
          confirm_token: input.confirm_token,
          multivalue_overrides: input.multivalue_overrides ?? null,
          delimiter: input.delimiter ?? null,
        },
      );
      return r.data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['pipeline', vars.pipeline_id, 'rows'] });
      qc.invalidateQueries({ queryKey: ['pipeline', vars.pipeline_id] });
      qc.invalidateQueries({ queryKey: ['pipeline'] });
    },
  });
}

export interface DeletePropertyInput {
  pipeline_id: string;
  property_key: string;
}

export interface DeletePropertyResponse {
  pipeline_id: string;
  property_key: string;
  affected_row_count: number;
}

export function useDeleteProperty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: DeletePropertyInput) => {
      const r = await api.delete<DeletePropertyResponse>(
        `/pipelines/${input.pipeline_id}/matching/properties/${encodeURIComponent(input.property_key)}`,
      );
      return r.data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['pipeline', vars.pipeline_id, 'rows'] });
      qc.invalidateQueries({ queryKey: ['pipeline', vars.pipeline_id] });
    },
  });
}

export interface PatchPropertyInput {
  pipeline_id: string;
  property_key: string;
  multivalue?: boolean | null;
  delimiter?: ',' | ';' | '|' | '\n' | null;
}

export interface PatchPropertyResponse {
  pipeline_id: string;
  property_key: string;
  multivalue: boolean;
  delimiter: string;
  presence: number;
}

export function usePatchProperty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: PatchPropertyInput) => {
      const r = await api.patch<PatchPropertyResponse>(
        `/pipelines/${input.pipeline_id}/matching/properties/${encodeURIComponent(input.property_key)}`,
        {
          multivalue: input.multivalue ?? null,
          delimiter: input.delimiter ?? null,
        },
      );
      return r.data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['pipeline', vars.pipeline_id, 'rows'] });
      qc.invalidateQueries({ queryKey: ['pipeline', vars.pipeline_id] });
    },
  });
}

// === matching-v2 §D.3: discovered properties queue + per-dataset blacklist ===

export interface DiscoveredPropertyItem {
  key: string;
  frequency: number;
}

export interface DiscoveredPropertiesResponse {
  dataset_id: string;
  items: DiscoveredPropertyItem[];
  total: number;
}

export interface DiscoveryDecision {
  key: string;
  action: 'add' | 'skip';
}

export interface DiscoveryDecideResponse {
  dataset_id: string;
  added: string[];
  skipped: string[];
  blacklist: string[];
  added_manual_properties: Array<{
    name: string;
    unit: string | null;
    allowed_values: string[] | null;
    mode: string;
  }>;
}

/** GET /datasets/{id}/matching/discovered-properties — очередь новых свойств. */
export function useDiscoveredProperties(datasetId: string | null) {
  return useQuery({
    queryKey: ['discovered-properties', datasetId],
    enabled: datasetId != null,
    staleTime: 0,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const r = await api.get<DiscoveredPropertiesResponse>(
        `/datasets/${datasetId}/matching/discovered-properties`,
      );
      return r.data;
    },
  });
}

/** POST /datasets/{id}/matching/discovered-properties/decide — add/skip batch. */
export function useDecideDiscoveredProperties(datasetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (decisions: DiscoveryDecision[]) => {
      const r = await api.post<DiscoveryDecideResponse>(
        `/datasets/${datasetId}/matching/discovered-properties/decide`,
        { decisions },
      );
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['discovered-properties', datasetId] });
      // decisions alter pipeline composition/blacklist → rows refetch
      qc.invalidateQueries({ queryKey: ['pipeline'] });
    },
  });
}
